import api from "../axios.js";

const state = () => ({
  appliedTimes: {}, // { [companyId]: { [date]: [times] } }
  bookedTimes: {}, // { [companyId]: { [date]: [times] } }
  etags: {}, // { [resource]: etag }
});

const getters = {
  getAppliedTimes: (state) => (companyId, date) =>
    state.appliedTimes?.[companyId]?.[date] || [],
  getBookedTimes: (state) => (companyId, date) =>
    state.bookedTimes?.[companyId]?.[date] || [],
};

const mutations = {
  setAppliedTimes(state, { companyId, date, times }) {
    state.appliedTimes = {
      ...state.appliedTimes,
      [companyId]: { ...(state.appliedTimes[companyId] || {}), [date]: times },
    };
  },
  setBookedTimes(state, { companyId, date, times }) {
    state.bookedTimes = {
      ...state.bookedTimes,
      [companyId]: { ...(state.bookedTimes[companyId] || {}), [date]: times },
    };
  },
  setETag(state, { resource, value }) {
    state.etags = { ...state.etags, [resource]: value };
  },
};

const actions = {
  async fetchTimes({ commit, state, rootState }, { companyId, date }) {
    const appliedResource = `contact-meetings-applied-${companyId}-${date}`;
    const bookedResource = `contact-meetings-booked-${companyId}-${date}`;

    const appliedHeaders = { Authorization: `Bearer ${rootState.token}` };
    if (state.etags[appliedResource]) {
      appliedHeaders["If-None-Match"] = state.etags[appliedResource];
    }

    const bookedHeaders = { Authorization: `Bearer ${rootState.token}` };
    if (state.etags[bookedResource]) {
      bookedHeaders["If-None-Match"] = state.etags[bookedResource];
    }

    try {
      const [appliedRes, bookedRes] = await Promise.all([
        api.get(
          `${process.env.VUE_APP_API_BASE_URL}/contact-meetings/user-applied/${companyId}/${date}`,
          {
            headers: appliedHeaders,
            validateStatus: (status) => status === 200 || status === 304,
          }
        ),
        api.get(
          `${process.env.VUE_APP_API_BASE_URL}/contact-meetings/booked-meetings/company/${companyId}/${date}`,
          {
            headers: bookedHeaders,
            validateStatus: (status) => status === 200 || status === 304,
          }
        ),
      ]);

      // Applied times
      if (appliedRes.status === 200) {
        //Ändringar
        commit("setAppliedTimes", { companyId, date, times: appliedRes.data });
        if (appliedRes.headers.etag) {
          commit("setETag", {
            resource: appliedResource,
            value: appliedRes.headers.etag,
          });
        }
      } else if (appliedRes.status === 304) {
        // Inga ändringar
      }

      // Booked times
      if (bookedRes.status === 200) {
        const times = bookedRes.data.map((m) => m.time);
        //Ändringar
        commit("setBookedTimes", { companyId, date, times });
        if (bookedRes.headers.etag) {
          commit("setETag", {
            resource: bookedResource,
            value: bookedRes.headers.etag,
          });
        }
      } else if (bookedRes.status === 304) {
        //Inga ändringar
      }
    } catch (err) {
      console.error("Misslyckades att hämta mötestider:", err);
      throw err;
    }
  },

  async submitMeetings(
    { dispatch, commit, rootState },
    { companyId, date, times }
  ) {
    await api.post(
      `${process.env.VUE_APP_API_BASE_URL}/contact-meetings`,
      { companyId, date, times },
      { headers: { Authorization: `Bearer ${rootState.token}` } }
    );

    // Nollställ ETag så att nästa fetch hämtar senaste data
    commit("setETag", {
      resource: `contact-meetings-applied-${companyId}-${date}`,
      value: null,
    });
    commit("setETag", {
      resource: `contact-meetings-booked-${companyId}-${date}`,
      value: null,
    });

    await dispatch("fetchTimes", { companyId, date });
  },

  async deleteMeetings(
    { dispatch, commit, rootState },
    { companyId, date, times }
  ) {
    await api.delete(`${process.env.VUE_APP_API_BASE_URL}/contact-meetings`, {
      data: { companyId, date, times },
      headers: { Authorization: `Bearer ${rootState.token}` },
    });

    // Nollställ ETag så att nästa fetch hämtar senaste data
    commit("setETag", {
      resource: `contact-meetings-applied-${companyId}-${date}`,
      value: null,
    });
    commit("setETag", {
      resource: `contact-meetings-booked-${companyId}-${date}`,
      value: null,
    });

    await dispatch("fetchTimes", { companyId, date });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
